import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { PropertyContactInfo } from '@vibesites/theme-preferred';
import { fillWeekHours } from './officeHours';

const PREFIX = 'scheduleatour';

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

// Office-hours collection row shape (mirrors PropertyContactInfo's OfficeHourEntry,
// which the parent theme does not re-export through its package entry).
interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

const addressSlotDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text:    { label: 'Address Text', type: 'text', defaultField: 'property_full_address' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (PropertyContactInfo). Editor overrides still win.
    url:     { label: 'Map URL',      type: 'url' },
    _target: { label: 'Target',       type: 'text', defaultValue: '_blank' },
  },
};

export function ScheduleATour() {
  const visitLabel = useContentToken(`${PREFIX}.visit_label`);
  const callLabel  = useContentToken(`${PREFIX}.call_label`);
  const hoursLabel = useContentToken(`${PREFIX}.hours_label`);
  const addressSlot = useSlotGroup(`${PREFIX}.address`, addressSlotDef);
  const phoneToken = useContentToken(`${PREFIX}.phone`);
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;

  const officeHoursRaw = useCollectionBinding<OfficeHourEntry>(`${PREFIX}.office_hours`, {
    collection: 'office_hours',
  });
  // Pad to a full week so days without hours (e.g. Sat/Sun) render as "Closed".
  const officeHours = fillWeekHours(officeHoursRaw);

  // Map location — prefer precise coordinates, fall back to the full address.
  const latitude = usePropertyField('property_address_latitude') as string | null;
  const longitude = usePropertyField('property_address_longitude') as string | null;
  const fullAddress = usePropertyField('property_full_address') as string | null;
  const mapQuery = latitude && longitude ? `${latitude},${longitude}` : (fullAddress || '');
  // Google Maps embed via the keyless `output=embed` endpoint.
  const mapSrc = mapQuery
    ? `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=15&output=embed`
    : null;
  const mapTitle = fullAddress ? `Map of ${fullAddress}` : 'Property location map';

  return (
    <section className="sat-section">
      <div className="sat-container">
        <div className="sat-grid">
          <div className="sat-info-col">
            <PropertyContactInfo
              prefix={PREFIX}
              visitLabel={visitLabel}
              callLabel={callLabel}
              hoursLabel={hoursLabel}
              address={addressSlot.text}
              addressUrl={addressSlot.url}
              addressTarget={addressSlot._target}
              phone={phone}
              officeHours={officeHours}
              addressGroupPath={`${PREFIX}.address`}
              addressGroupDef={addressSlotDef}
            />
          </div>

          <div className="sat-map-col">
            {mapSrc ? (
              <iframe
                className="sat-map"
                src={mapSrc}
                title={mapTitle}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            ) : (
              <div className="sat-map sat-map--empty">
                <p>Map location is not available for this property.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .sat-section {
          padding: 2.5rem 0 3.5rem;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .sat-section { padding: 3rem 0 4rem; }
        }
        .sat-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sat-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sat-container { padding: 0 2rem; } }

        .sat-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .sat-grid {
            grid-template-columns: 5fr 7fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        .sat-info-col :global(.pci-block-section) { padding: 0; }
        .sat-info-col :global(.pci-block-container) { padding: 0; max-width: none; }

        .sat-map-col { position: relative; }
        .sat-map {
          display: block;
          width: 100%;
          aspect-ratio: 4 / 3;
          min-height: 360px;
          border: 0;
          border-radius: var(--vibe-radius-lg, 0.75rem);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
        }
        @media (min-width: 1024px) {
          .sat-map { aspect-ratio: auto; height: 100%; min-height: 460px; }
        }
        .sat-map--empty {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          text-align: center;
          background: color-mix(in srgb, var(--vibe-color-border) 25%, transparent);
          color: var(--vibe-color-text-muted);
          font-family: var(--vibe-font-body);
        }
      `}</style>

      {/* Schema-lint anchors — PropertyContactInfo uses dynamic `${prefix}.*` paths
          on its EditableTokens/EditableSlotGroups, so these hidden elements satisfy
          token-missing-editable-wrapper / slot-group-missing-editable-wrapper. */}
      <EditableToken path="scheduleatour.visit_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.call_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.phone"       as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.hours_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      {/* Slot-group anchor — satisfies slot-group-missing-editable-wrapper for the dynamic prefix path in PropertyContactInfo */}
      <div style={{ display: 'none' }}>
        <EditableSlotGroup groupPath="scheduleatour.address" definition={addressSlotDef}>
          <span />
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleATour',
  displayName: 'Schedule A Tour',
  tokens: {
    'scheduleatour.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduleatour.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduleatour.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
    'scheduleatour.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduleatour.address': addressSlotDef,
  },
  collectionBindings: {
    'scheduleatour.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleATour as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleATour;
